import { api, errorBanner, loadingBanner } from "../api.js";
import { replaceQuery } from "../router.js";
import {
  agreeMark,
  escapeHtml,
  fmtDate,
  fmtDateTime,
  fmtInt,
  houseLabel,
  legislatureRange,
  memberNoun,
  propositionLabel,
  propositionUrl,
  rollCallUrl,
  sourceMixLabel,
  truncate,
  votePill,
} from "../format.js";
import { renderStatCard, renderMiniStat } from "../components/stats.js";
import { renderGauge } from "../components/charts.js";
import { renderPagination, bindPagination } from "../components/pagination.js";
import { renderPropositionCard } from "../components/propcard.js";

export async function renderMember({ root, match, query, isCurrent }) {
  const id = match[1];
  const state = {
    legislature: query.legislature || "",
    only_disagreements: query.only_disagreements === "1",
    page: 1,
  };
  const m = await api.member(id, { legislature: state.legislature });
  if (!isCurrent()) return;
  document.title = `${m.name} · Como Vota`;
  const st = m.stats;
  const leg = st?.legislature ?? (m.legislatures?.length ? m.legislatures[m.legislatures.length - 1] : null);
  const noun = memberNoun(m.house, false);

  const legOptions = [...(m.legislatures || [])]
    .sort((a, b) => b - a)
    .map((n) => `<option value="${n}" ${leg === n ? "selected" : ""}>${n}ª legislatura (${legislatureRange(n)})</option>`)
    .concat(`<option value="0" ${leg === 0 ? "selected" : ""}>Todo o período</option>`)
    .join("");

  root.innerHTML = `
    <article class="detail detail--wide">
      <a class="back-link" href="/parlamentares" data-link>← Parlamentares</a>
      <header class="detail__head detail__head--person">
        ${m.photo_url ? `<img class="person-photo" src="${escapeHtml(m.photo_url)}" alt="" />` : `<span class="person-photo person-photo--empty"></span>`}
        <div>
          <span class="eyebrow-pill">${escapeHtml(houseLabel(m.house))}</span>
          <h1 class="page-title">${escapeHtml(m.name)}</h1>
          ${m.full_name && m.full_name !== m.name ? `<p class="person-fullname">${escapeHtml(m.full_name)}</p>` : ""}
          <div class="detail__meta">
            ${m.party_sigla ? `<span class="pill pill--sage">${escapeHtml(m.party_sigla)}</span>` : ""}
            ${m.uf ? `<span class="pill pill--senado">${escapeHtml(m.uf)}</span>` : ""}
            ${m.in_exercise === false ? `<span class="pill pill--neutral">fora de exercício</span>` : ""}
            ${m.email ? `<a class="inline-link" href="mailto:${escapeHtml(m.email)}">${escapeHtml(m.email)}</a>` : ""}
            ${m.website ? `<a class="inline-link" href="${escapeHtml(m.website)}" target="_blank" rel="noopener">site ↗</a>` : ""}
          </div>
        </div>
      </header>

      ${renderVoteCounts(m.vote_counts, state.legislature === "0" ? 0 : st?.legislature ?? null)}

      <section class="detail__section">
        <h2 class="section-title">Mandatos</h2>
        ${renderMandates(m)}
      </section>

      <section class="detail__section">
        <div class="section-head">
          <h2 class="section-title">Estatísticas</h2>
          <label class="select-inline">Legislatura <select data-leg>${legOptions}</select></label>
        </div>
        ${st ? renderStats(st, m) : `<p class="muted">Ainda não há estatísticas calculadas para este ${noun}.</p>`}
      </section>

      <section class="detail__section">
        <div class="section-head">
          <h2 class="section-title">Histórico de Votos</h2>
          <label class="check"><input type="checkbox" data-only-dis ${state.only_disagreements ? "checked" : ""} /> apenas divergências</label>
        </div>
        <div data-votes>${loadingBanner()}</div>
      </section>

      <section class="detail__section">
        <h2 class="section-title">Proposições de sua autoria</h2>
        <div class="tabs" data-role-tabs>
          <button type="button" class="tab tab--active" data-role="first">Como 1º autor</button>
          <button type="button" class="tab" data-role="co">Como coautor</button>
        </div>
        <div data-props>${loadingBanner()}</div>
      </section>
    </article>`;

  root.querySelector("[data-leg]").addEventListener("change", (e) => {
    replaceQuery({ legislature: e.target.value, only_disagreements: state.only_disagreements ? "1" : "" });
    window.dispatchEvent(new PopStateEvent("popstate"));
  });

  const votesEl = root.querySelector("[data-votes]");
  root.querySelector("[data-only-dis]").addEventListener("change", (e) => {
    state.only_disagreements = e.target.checked;
    state.page = 1;
    replaceQuery({ legislature: state.legislature, only_disagreements: state.only_disagreements ? "1" : "" });
    loadVotes();
  });

  async function loadVotes() {
    votesEl.innerHTML = loadingBanner();
    try {
      const page = await api.memberVotes(id, {
        legislature: leg ?? "",
        only_disagreements: state.only_disagreements ? "true" : "",
        page: state.page,
        page_size: 25,
      });
      if (!isCurrent()) return;
      if (!page.items?.length) {
        votesEl.innerHTML = `<p class="empty-card">${state.only_disagreements ? "Nenhuma divergência registrada." : "Nenhum voto registrado."}</p>`;
        return;
      }
      votesEl.innerHTML = `
        <div class="table-wrap">
          <table class="table table--votes">
            <thead><tr><th>Votação</th><th>Voto</th><th>Partido</th><th>Governo</th></tr></thead>
            <tbody>${page.items.map(renderVoteRow).join("")}</tbody>
          </table>
        </div>
        <p class="meta-muted">✓ de acordo · ✗ divergente · orientação do partido/governo entre parênteses; “plur.” = maioria dos colegas de partido quando não há orientação.</p>
        ${renderPagination(page)}`;
      bindPagination(votesEl, (n) => {
        state.page = n;
        loadVotes();
      });
    } catch (err) {
      if (!isCurrent()) return;
      votesEl.innerHTML = errorBanner(err);
    }
  }

  const propsEl = root.querySelector("[data-props]");
  let role = "first";
  root.querySelectorAll("[data-role]").forEach((btn) =>
    btn.addEventListener("click", () => {
      role = btn.dataset.role;
      root.querySelectorAll("[data-role]").forEach((b) => b.classList.toggle("tab--active", b === btn));
      loadProps(1);
    }),
  );
  async function loadProps(page) {
    propsEl.innerHTML = loadingBanner();
    try {
      const data = await api.memberPropositions(id, { role, page, page_size: 10 });
      if (!isCurrent()) return;
      propsEl.innerHTML = data.items?.length
        ? `<ul class="card-grid">${data.items.map((p) => renderPropositionCard(p)).join("")}</ul>${renderPagination(data)}`
        : `<p class="empty-card">Nenhuma proposição ${role === "first" ? "como primeiro autor" : "como coautor"}.</p>`;
      bindPagination(propsEl, loadProps);
    } catch (err) {
      if (!isCurrent()) return;
      propsEl.innerHTML = errorBanner(err);
    }
  }

  await Promise.all([loadVotes(), loadProps(1)]);
}

function renderVoteCounts(vc, legislature) {
  if (!vc) return "";
  const scope = legislature === 0 ? "todo o período" : legislature ? `${legislature}ª legislatura` : "legislatura mais recente";
  return `
    <section class="stat-row" aria-label="Totais de votos">
      ${renderMiniStat("Votos registrados", fmtInt(vc.total), "Votações nominais em que o nome consta na lista")}
      ${renderMiniStat("Presenças", fmtInt(vc.present), "Votos registrados menos ausências")}
      ${renderMiniStat("Sim", fmtInt(vc.sim))}
      ${renderMiniStat("Não", fmtInt(vc.nao))}
    </section>
    <p class="meta-muted stat-row__caption">Totais brutos na ${escapeHtml(scope)}${vc.abstencao ? ` · ${fmtInt(vc.abstencao)} abstenções` : ""}${vc.obstrucao ? ` · ${fmtInt(vc.obstrucao)} obstruções` : ""}${vc.ausente ? ` · ${fmtInt(vc.ausente)} ausências` : ""}</p>`;
}

function renderMandates(m) {
  const mandates = m.mandates || [];
  const affs = m.affiliations || [];
  if (!mandates.length && !affs.length) return `<p class="muted">Sem histórico de mandatos.</p>`;
  const rows = mandates
    .map(
      (x) => `<li class="tl tl--${x.end_date ? "done" : "current"}"><span class="tl__dot"></span><div class="tl__body">
        <strong>${x.legislature}ª legislatura</strong> <span class="meta-muted">${legislatureRange(x.legislature)}</span>
        <span class="pill pill--${x.condition === "suplente" ? "warn" : "ok"}">${escapeHtml(x.condition)}</span>
        <p class="muted">${fmtDate(x.start_date)} – ${x.end_date ? fmtDate(x.end_date) : "em exercício"}${x.uf ? ` · ${escapeHtml(x.uf)}` : ""}${x.end_reason ? ` · ${escapeHtml(x.end_reason)}` : ""}</p>
      </div></li>`,
    )
    .join("");
  const parties = affs.length
    ? `<p class="muted"><strong>Filiações:</strong> ${affs
        .map((a) => `${escapeHtml(a.party_sigla)} (${fmtDate(a.start_date)}${a.end_date ? ` – ${fmtDate(a.end_date)}` : " – atual"})`)
        .join(" · ")}</p>`
    : "";
  return `<ol class="timeline">${rows}</ol>${parties}`;
}

function renderStats(st, m) {
  const house = m.house;
  const cards = [
    renderStatCard(st.attendance, {
      label: "Presença em votações",
      kind: "pct",
      house,
      hint: "Votações nominais em que registrou presença (voto, abstenção ou obstrução) sobre as que poderia votar.",
    }),
    st.session_attendance
      ? renderStatCard(st.session_attendance, {
          label: "Presença em sessões",
          kind: "pct",
          house,
          hint: "Sessões deliberativas do Plenário com presença registrada.",
        })
      : "",
    renderStatCard(st.props_per_year, {
      label: "Proposições por ano",
      kind: "number",
      house,
      extra: `<div class="totals"><span class="total-pill"><strong>${st.props_first_author ?? 0}</strong> 1º autor</span><span class="total-pill"><strong>${st.props_coauthor ?? 0}</strong> coautor</span></div>`,
      hint: "Projetos (PL, PLP, PEC, PDL…) apresentados como primeiro autor, por ano de mandato.",
    }),
  ].join("");
  const govBadge = sourceMixLabel(st.gov_orientation?.source_mix);
  const gauges = [
    renderGauge(st.party_loyalty?.value, {
      label: "Fidelidade ao partido",
      n: st.party_loyalty?.n,
      medianHouse: st.party_loyalty?.house_median,
      medianParty: st.party_loyalty?.party_median,
      sublabel: "vota como a maioria do seu partido",
    }),
    renderGauge(st.party_orientation?.value, {
      label: "Segue orientação do partido",
      n: st.party_orientation?.n,
      medianHouse: st.party_orientation?.house_median,
      medianParty: st.party_orientation?.party_median,
    }),
    renderGauge(st.gov_orientation?.value, {
      label: "Segue orientação do governo",
      n: st.gov_orientation?.n,
      medianHouse: st.gov_orientation?.house_median,
      medianParty: st.gov_orientation?.party_median,
      badge: govBadge,
      sublabel: govBadge?.variant === "warn" ? "inclui orientação inferida (proxy)" : "",
    }),
  ].join("");
  return `
    <div class="stats stats--grid">${cards}</div>
    <div class="gauges">${gauges}</div>
    <p class="meta-muted">${st.legislature === 0 ? "Todo o período" : `${st.legislature}ª legislatura`}${st.days_in_mandate ? ` · ${st.days_in_mandate} dias de mandato` : ""}${st.computed_at ? ` · calculado em ${fmtDate(st.computed_at)}` : ""} · <a class="inline-link" href="/metodologia" data-link>metodologia</a></p>`;
}

function renderVoteRow(row) {
  const rc = row.roll_call;
  const p = rc.proposition;
  const partyRef = row.party_orientation
    ? `${votePill(row.party_orientation, "pill--sm")}`
    : row.party_plurality
      ? `${votePill(row.party_plurality, "pill--sm")} <span class="meta-muted">plur.</span>`
      : `<span class="meta-muted">—</span>`;
  const govRef = row.gov_orientation
    ? `${votePill(row.gov_orientation, "pill--sm")}${row.gov_orientation_source && row.gov_orientation_source !== "official" ? ` <span class="meta-muted" title="${escapeHtml(row.gov_orientation_source)}">proxy</span>` : ""}`
    : `<span class="meta-muted">—</span>`;
  const agreesParty = row.agrees_party_orientation ?? row.agrees_party;
  return `<tr>
    <td>
      <a href="${rollCallUrl(rc)}" data-link class="vote-desc">${escapeHtml(truncate(rc.description, 140)) || "Votação"}</a>
      <div class="meta-muted">${fmtDateTime(rc.happened_at)}${p ? ` · <a class="inline-link" href="${propositionUrl(p)}" data-link>${escapeHtml(propositionLabel(p))}</a>` : ""}</div>
    </td>
    <td>${votePill(row.vote)}</td>
    <td>${agreeMark(agreesParty)} ${partyRef}</td>
    <td>${agreeMark(row.agrees_gov)} ${govRef}</td>
  </tr>`;
}

